import React, { useState } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 28px;
  max-width: 480px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #1a1f2e;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  font-size: 24px;
  padding: 4px;
  line-height: 1;
  
  &:hover {
    color: #64748b;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 18px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #2DBCAF;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #2DBCAF;
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 0;
  border-top: 1px solid #f1f5f9;
`;

const ToggleLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ToggleTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const ToggleDesc = styled.span`
  font-size: 12px;
  color: #94a3b8;
`;

const ToggleSwitch = styled.button<{ $active: boolean }>`
  width: 48px;
  height: 26px;
  border-radius: 13px;
  border: none;
  background: ${p => p.$active ? '#8b5cf6' : '#e2e8f0'};
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${p => p.$active ? '24px' : '3px'};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #fff;
    transition: left 0.2s;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
`;

const Button = styled.button<{ $primary?: boolean }>`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  ${p => p.$primary ? `
    background: #2DBCAF;
    color: #fff;
    border: none;
    
    &:hover:not(:disabled) {
      background: #26a89d;
    }
  ` : `
    background: #fff;
    color: #64748b;
    border: 1px solid #e2e8f0;
    
    &:hover:not(:disabled) {
      background: #f8fafc;
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SuccessMsg = styled.div`
  background: rgba(34, 197, 94, 0.1);
  color: #16a34a;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 16px;
`;

const ErrorMsg = styled.div`
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 16px;
`;

interface TKFeature {
  _id: string;
  slug: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  order: number;
  isActive: boolean;
  isPremium?: boolean;
}

interface Props {
  feature: TKFeature;
  onClose: () => void;
  onSaved: (updatedFeature: TKFeature) => void;
}

const EditFeatureModal: React.FC<Props> = ({ feature, onClose, onSaved }) => {
  const [name, setName] = useState(feature.name || '');
  const [description, setDescription] = useState(feature.description || '');
  const [isPremium, setIsPremium] = useState(feature.isPremium || false);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Nama fitur wajib diisi');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`/api/admin/tumbuh-kembang/feature/${feature._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim(),
          isPremium,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setSuccess(true);
        setTimeout(() => {
          onSaved(data.feature);
        }, 500);
      } else {
        const data = await res.json();
        setError(data.error || 'Gagal menyimpan');
      }
    } catch {
      setError('Terjadi kesalahan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <Header>
          <Title>✏️ Edit Fitur</Title>
          <CloseBtn onClick={onClose}>×</CloseBtn>
        </Header>

        {success && <SuccessMsg>✓ Berhasil disimpan!</SuccessMsg>}
        {error && <ErrorMsg>{error}</ErrorMsg>}

        <FormGroup>
          <Label>Slug</Label>
          <Input value={feature.slug} disabled style={{ background: '#f8fafc' }} />
        </FormGroup>

        <FormGroup>
          <Label>Nama Fitur *</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Agenda Stimulasi"
          />
        </FormGroup>

        <FormGroup>
          <Label>Deskripsi</Label>
          <TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Aktivitas stimulasi harian untuk anak..."
          />
        </FormGroup>

        <ToggleRow>
          <ToggleLabel>
            <ToggleTitle>💎 Fitur Premium</ToggleTitle>
            <ToggleDesc>Hanya tersedia untuk pengguna premium</ToggleDesc>
          </ToggleLabel>
          <ToggleSwitch
            $active={isPremium}
            onClick={() => setIsPremium(!isPremium)}
          />
        </ToggleRow>

        <Actions>
          <Button onClick={onClose} disabled={loading}>Batal</Button>
          <Button $primary onClick={handleSave} disabled={loading}>
            {loading ? 'Menyimpan...' : 'Simpan'}
          </Button>
        </Actions>
      </Modal>
    </Overlay>
  );
};

export default EditFeatureModal;
